/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { AgentDesktopApi } from './AgentDesktopBridge';
import { type AgentWorkspaceContextValue, getAgentWorkspacePath } from './AgentWorkspaceContext';
import { agentWorkspaceStore } from './agentWorkspaceStore';
import type { AgentWorkspaceWidgetSource, ExpandedAgentArtifact } from './types';
import { useAgentWorkspaceAutosave, useAgentWorkspaceServerCopy } from './useAgentWorkspaceSync';

/** Context value for the active conversation's workspace, plus the desktop handle it opens. */
export const useAgentWorkspaceValue = (conversationId: string | undefined) => {
  const desktopApiRef = useRef<AgentDesktopApi | null>(null);
  // A request counter with the id: expanding the focused widget again must still refocus it.
  const [focus, setFocus] = useState<{ widgetId?: string; request: number }>({ request: 0 });
  useAgentWorkspaceAutosave();
  // The header's workspace button needs the saved copy before the window ever opens.
  useAgentWorkspaceServerCopy(conversationId);

  const expandArtifact = useCallback(
    (
      artifact: ExpandedAgentArtifact,
      conversationTitle?: string,
      source?: AgentWorkspaceWidgetSource,
    ) => {
      // Artifacts only exist once a turn ran, which gives the conversation its id.
      if (!conversationId) return;
      const widgetId = agentWorkspaceStore.add(conversationId, artifact, conversationTitle, source);
      setFocus(({ request }) => ({ widgetId, request: request + 1 }));
      desktopApiRef.current?.openPath(getAgentWorkspacePath(conversationId));
    },
    [conversationId],
  );

  const openWorkspace = useCallback(() => {
    if (conversationId) desktopApiRef.current?.openPath(getAgentWorkspacePath(conversationId));
  }, [conversationId]);

  const value = useMemo<AgentWorkspaceContextValue>(
    () => ({
      conversationId,
      expandArtifact,
      focusedWidgetId: focus.widgetId,
      focusRequest: focus.request,
      openWorkspace,
    }),
    [conversationId, expandArtifact, focus, openWorkspace],
  );

  return { desktopApiRef, workspaceValue: value };
};
